import React, { useEffect, useRef, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { PDFCanvasViewport } from '@/features/pdf-editor/components/viewer/PDFCanvasViewport';
import { PDFViewerState } from '@/features/pdf-editor/types';
import { CropRegion } from '../utils/cropPdfRegion';

interface DrawingCropPickerProps {
  fileUrl: string;
  onRegionChange: (region: CropRegion | null) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

/**
 * Renders a PDF with pan/zoom (PDFCanvasViewport) and Shift+drag crop selection.
 * Fullscreen is controlled by the parent — this component just fills its container.
 */
export const DrawingCropPicker: React.FC<DrawingCropPickerProps> = ({
  fileUrl,
  onRegionChange,
  isFullscreen,
  onToggleFullscreen,
}) => {
  const [scale, setScale]       = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [pdfDimensions, setPdfDimensions] = useState<{ width: number; height: number } | null>(null);

  const [selectionPx, setSelectionPx] = useState<{
    left: number; top: number; width: number; height: number;
  } | null>(null);
  const [cropRegion, setCropRegion] = useState<CropRegion | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const dragStart    = useRef<{ x: number; y: number } | null>(null);

  // Keep mutable refs so drag handlers always read the latest values
  const positionRef = useRef(position);
  const scaleRef    = useRef(scale);
  useEffect(() => { positionRef.current = position; }, [position]);
  useEffect(() => { scaleRef.current    = scale;    }, [scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onMouseDown = (e: MouseEvent) => {
      if (!e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = container.getBoundingClientRect();
      dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      setIsSelecting(true);
      setSelectionPx(null);
      setCropRegion(null);
      onRegionChange(null);
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      setSelectionPx({
        left:   Math.min(dragStart.current.x, cx),
        top:    Math.min(dragStart.current.y, cy),
        width:  Math.abs(cx - dragStart.current.x),
        height: Math.abs(cy - dragStart.current.y),
      });
    };

    const onMouseUp = (e: MouseEvent) => {
      if (!dragStart.current) return;
      const rect = container.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const { x: sx, y: sy } = dragStart.current;
      dragStart.current = null;
      setIsSelecting(false);

      const pixLeft   = Math.min(sx, cx);
      const pixTop    = Math.min(sy, cy);
      const pixRight  = Math.max(sx, cx);
      const pixBottom = Math.max(sy, cy);

      if (pixRight - pixLeft < 10 || pixBottom - pixTop < 10) {
        setSelectionPx(null);
        onRegionChange(null);
        return;
      }

      const pos   = positionRef.current;
      const scale = scaleRef.current;
      const tl = { x: (pixLeft  - pos.x) / scale, y: (pixTop    - pos.y) / scale };
      const br = { x: (pixRight - pos.x) / scale, y: (pixBottom - pos.y) / scale };

      const pdfW = pdfDimensions?.width  ?? Infinity;
      const pdfH = pdfDimensions?.height ?? Infinity;
      const region: CropRegion = {
        x:      Math.max(0, Math.round(tl.x)),
        y:      Math.max(0, Math.round(tl.y)),
        width:  Math.min(Math.round(br.x - tl.x), pdfW),
        height: Math.min(Math.round(br.y - tl.y), pdfH),
      };
      setCropRegion(region);
      onRegionChange(region);
    };

    container.addEventListener('mousedown', onMouseDown, { capture: true });
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      container.removeEventListener('mousedown', onMouseDown, { capture: true });
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [pdfDimensions, onRegionChange]);

  const viewerState: PDFViewerState = {
    currentPage: 1, numPages: 1, rotation: 0, scale, position,
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-gray-100 rounded-lg border border-gray-200"
      style={{ cursor: isSelecting ? 'crosshair' : undefined }}
    >
      <PDFCanvasViewport
        fileUrl={fileUrl}
        viewerState={viewerState}
        onLoadSuccess={() => {}}
        onScaleChange={setScale}
        onPositionChange={setPosition}
        onFitComplete={(s, p) => { setScale(s); setPosition(p); }}
        onDimensionsLoad={setPdfDimensions}
        loadingMessage="Loading Drawing..."
      />

      {/* Maximize / minimize button */}
      <button
        onClick={onToggleFullscreen}
        className="absolute top-2 right-2 z-30 rounded-md bg-white/80 backdrop-blur-sm border border-gray-200 p-1.5 text-gray-600 hover:text-gray-900 hover:bg-white shadow-sm transition-colors"
        title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand canvas'}
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>

      {/* Selection rectangle */}
      {selectionPx && selectionPx.width > 0 && selectionPx.height > 0 && (
        <div style={{
          position: 'absolute',
          left: selectionPx.left, top: selectionPx.top,
          width: selectionPx.width, height: selectionPx.height,
          border: '2px solid #2563eb',
          backgroundColor: 'rgba(37,99,235,0.12)',
          pointerEvents: 'none', zIndex: 20,
        }} />
      )}

      {/* Dimension label */}
      {cropRegion && selectionPx && (
        <div style={{
          position: 'absolute',
          left: selectionPx.left,
          top: Math.max(0, selectionPx.top - 26),
          pointerEvents: 'none', zIndex: 21,
        }}>
          <span className="bg-blue-600 text-white text-xs font-medium px-2 py-0.5 rounded shadow">
            {Math.round(cropRegion.width)} × {Math.round(cropRegion.height)} pt
          </span>
        </div>
      )}

      {/* Status bar */}
      <div className="absolute bottom-2 left-0 right-0 flex justify-center pointer-events-none z-30">
        {cropRegion ? (
          <span className="bg-blue-600/90 text-white text-xs font-medium px-3 py-1 rounded-full shadow">
            ✓ {Math.round(cropRegion.width)} × {Math.round(cropRegion.height)} pt · Shift+drag to re-select
          </span>
        ) : (
          <span className="bg-black/50 text-white text-xs px-3 py-1 rounded-full">
            Drag to pan · Scroll to zoom · <kbd className="font-mono opacity-80">Shift</kbd>+drag to select
          </span>
        )}
      </div>
    </div>
  );
};
